import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Download, Loader2, Plus, RefreshCw, Sparkles, Trash2, X } from 'lucide-react';
import { db } from '../../utils/firebase';
import type { EnhancedDeal, ProposalBuilderData, TimberProject } from '../../types';
import {
  SURETY_BOND_TYPE_LABELS,
  SURETY_OBLIGEE_ROLE_LABELS,
  type SuretyBondRequest,
  type SuretyBondType,
  type SuretyBondingSettings,
  type SuretyObligeeRole,
} from '../../types/regulatory';
import {
  SURETY_FIELD_HELP,
  activeContractRange,
  buildSuretyBondRequestDraft,
  computeBondAmount,
  defaultBondPercent,
  defaultScopeOfWork,
  formatBondMoney,
  longDateFromYmd,
  validateSuretyBondRequest,
  type SuretyBondRequestDraft,
} from '../../utils/suretyBondRequest';
import { computeProposalContractRange, describeContractRangeBasis } from '../../utils/proposalContractRange';
import {
  isGeneratedEnclosureLabel,
  mergeEnclosureSelections,
  needsScheduleBeforeDownload,
  scheduleCompletionDate,
  scheduleStartDate,
  wantsContractSummaryEnclosure,
  type SchedulePhaseTaskLike,
} from '../../utils/suretyEnclosures';
import { pickTimberProjectForDealSchedule } from '../../utils/paymentDueFlags';
import { getProjectDisplayName } from '../../utils/projectName';
import { classifyPLDealGroup, pickPLDeals, type ProjectPLGroup } from '../../utils/qbDealLinkOptions';
import type { TaskGroupLike, TaskScheduleLike } from '../../utils/paymentDueFlags';
import FieldHelp from './FieldHelp';
import SuretyEnclosureWizardModal, { type SuretyEnclosureWizardResult } from './SuretyEnclosureWizardModal';

export interface SuretyBondRequestModalProps {
  /** Existing request to edit; omit for a new one. */
  existing?: SuretyBondRequest | null;
  deals: EnhancedDeal[];
  timberProjects: TimberProject[];
  /** Hub tasks (need startDate too so the schedule enclosure can span phases). */
  scheduleTasks: Array<TaskScheduleLike & SchedulePhaseTaskLike>;
  taskGroups: TaskGroupLike[];
  settings: SuretyBondingSettings;
  onClose: () => void;
  onSave: (draft: SuretyBondRequestDraft, opts: { download: boolean }) => Promise<void>;
  /**
   * Open already heading for "Save and download PDF" — used by the list's
   * Download button when the request still needs its schedule enclosure built.
   */
  autoDownload?: boolean;
}

type RangeFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'none'; reason: string }
  | { status: 'ok'; basis: string; scenarios: number }
  | { status: 'error'; message: string };

function numStr(n: number | null): string {
  return n == null ? '' : String(n);
}

function parseNumOrNull(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function emptyDraft(settings: SuretyBondingSettings): SuretyBondRequestDraft {
  const stub = { id: '', title: '', value: 0, contactName: '', location: '' } as unknown as EnhancedDeal;
  const d = buildSuretyBondRequestDraft({ deal: stub, project: null, settings });
  return { ...d, projectLabel: '', scopeOfWork: '', obligee: { ...d.obligee, name: '', address: '' }, siteAddress: '' };
}

const SuretyBondRequestModal: React.FC<SuretyBondRequestModalProps> = ({
  existing,
  deals,
  timberProjects,
  scheduleTasks,
  taskGroups,
  settings,
  onClose,
  onSave,
  autoDownload = false,
}) => {
  // Under-contract jobs plus anything with an open estimating / precon Hub
  // project — bid bonds are requested long before a contract is signed.
  const projectGroups = useMemo(() => {
    const groups: Record<ProjectPLGroup, { id: string; label: string }[]> = {
      contracted: [],
      preconstruction: [],
      estimating: [],
    };
    // Completed jobs cannot be bonded — only deals with an open Hub project.
    const openDealIds = new Set(
      timberProjects.filter((p) => p.dealId && p.status !== 'completed').map((p) => p.dealId as string),
    );
    for (const d of pickPLDeals(deals, timberProjects)) {
      if (!d.id || !openDealIds.has(d.id)) continue;
      groups[classifyPLDealGroup(d, timberProjects)].push({
        id: d.id,
        label: getProjectDisplayName(d) || d.title || d.id || 'Project',
      });
    }
    (Object.keys(groups) as ProjectPLGroup[]).forEach((k) => groups[k].sort((a, b) => a.label.localeCompare(b.label)));
    return groups;
  }, [deals, timberProjects]);

  const initial = useMemo<SuretyBondRequestDraft>(() => {
    if (existing) {
      const { id: _id, createdAt, createdBy, updatedAt, updatedBy, sentAt, issuedAt, ...rest } = existing;
      void _id; void createdAt; void createdBy; void updatedAt; void updatedBy; void sentAt; void issuedAt;
      return rest;
    }
    return emptyDraft(settings);
  }, [existing, settings]);

  const [draft, setDraft] = useState<SuretyBondRequestDraft>(initial);
  const [contractAmountInput, setContractAmountInput] = useState(initial.contractAmount ? String(initial.contractAmount) : '');
  const [bondPercentInput, setBondPercentInput] = useState(String(initial.bondPercent));
  const [retainageInput, setRetainageInput] = useState(numStr(initial.retainagePercent));
  const [ldInput, setLdInput] = useState(numStr(initial.liquidatedDamagesPerDay));
  const [subPctInput, setSubPctInput] = useState(numStr(initial.subcontractedPercent));
  const [warrantyInput, setWarrantyInput] = useState(String(initial.warrantyMonths));
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState<'save' | 'download' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 'manual' = opened from the Enclosures fieldset; 'predownload' = the
  // schedule is listed but not built yet and the user asked for the PDF.
  const [wizard, setWizard] = useState<null | 'manual' | 'predownload'>(null);
  const [rangeMinInput, setRangeMinInput] = useState(initial.contractRange ? String(initial.contractRange.min) : '');
  const [rangeFetch, setRangeFetch] = useState<RangeFetchState>({ status: 'idle' });
  const rangeFetchSeq = useRef(0);

  useEffect(() => {
    setDraft(initial);
    setContractAmountInput(initial.contractAmount ? String(initial.contractAmount) : '');
    setBondPercentInput(String(initial.bondPercent));
    setRetainageInput(numStr(initial.retainagePercent));
    setLdInput(numStr(initial.liquidatedDamagesPerDay));
    setSubPctInput(numStr(initial.subcontractedPercent));
    setWarrantyInput(String(initial.warrantyMonths));
    setRangeMinInput(initial.contractRange ? String(initial.contractRange.min) : '');
  }, [initial]);

  const patch = (changes: Partial<SuretyBondRequestDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...changes };
      next.bondAmount = computeBondAmount(next.contractAmount, next.bondPercent);
      return next;
    });
  };

  /**
   * Read the deal's proposal and, when the client still has selections that
   * move the price, quote the low/high as a range with the contract amount
   * pinned to the high end. Signed proposals (or none) leave the amount alone.
   */
  const fetchRange = useCallback(async (dealId: string, { announce }: { announce: boolean }) => {
    if (!dealId) return;
    const seq = ++rangeFetchSeq.current;
    setRangeFetch({ status: 'loading' });
    try {
      const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
      if (seq !== rangeFetchSeq.current) return;
      if (!snap.exists()) {
        setRangeFetch({ status: 'none', reason: 'No proposal on this deal — the amount comes from the deal value.' });
        if (announce) patch({ contractRange: null });
        return;
      }
      const range = computeProposalContractRange(snap.data() as ProposalBuilderData);
      if (!range) {
        setRangeFetch({ status: 'none', reason: 'The proposal is signed or has no client selections left, so the price is fixed.' });
        if (announce) patch({ contractRange: null });
        return;
      }
      const basis = describeContractRangeBasis(range.dimensions);
      setRangeFetch({ status: 'ok', basis, scenarios: range.scenarios });
      setRangeMinInput(String(range.min));
      setContractAmountInput(String(range.max));
      patch({ contractRange: { min: range.min, max: range.max, basis }, contractAmount: range.max });
    } catch (err) {
      if (seq !== rangeFetchSeq.current) return;
      setRangeFetch({ status: 'error', message: err instanceof Error ? err.message : 'Could not read the proposal' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickProject = (dealId: string) => {
    const deal = deals.find((d) => d.id === dealId);
    rangeFetchSeq.current += 1;
    setRangeFetch({ status: 'idle' });
    setRangeMinInput('');
    if (!deal) {
      patch({ dealId: '', projectId: '', projectLabel: '', projectNumber: '', contractRange: null });
      return;
    }
    const project = pickTimberProjectForDealSchedule(timberProjects, dealId, scheduleTasks, taskGroups) ?? null;
    // Estimating / precon jobs default to a bid bond; signed work to P&P. The toggle still overrides.
    const group = classifyPLDealGroup(deal, timberProjects);
    const bondType: SuretyBondType = group === 'contracted' ? 'performance_payment' : 'bid';
    const prefill = buildSuretyBondRequestDraft({ deal, project, settings, bondType });
    // Keep the letter date, agent, signer, and anything typed about the bond itself.
    const next: SuretyBondRequestDraft = {
      ...prefill,
      letterDate: draft.letterDate,
      agent: draft.agent,
      signer: draft.signer,
      bondForm: draft.bondForm,
      neededBy: draft.neededBy,
      notes: draft.notes,
      // Generated enclosures belong to the old project — drop them, keep hand-typed rows.
      enclosures: draft.enclosures.filter((s) => !isGeneratedEnclosureLabel(s)),
      enclosureDocs: null,
      contractRange: null,
    };
    setDraft(next);
    setContractAmountInput(next.contractAmount ? String(next.contractAmount) : '');
    setBondPercentInput(String(next.bondPercent));
    setWarrantyInput(String(next.warrantyMonths));
    void fetchRange(dealId, { announce: false });
  };

  const rangeOn = !!draft.contractRange;
  const toggleRange = (on: boolean) => {
    if (!on) {
      patch({ contractRange: null });
      return;
    }
    const max = draft.contractAmount || 0;
    const min = Number(rangeMinInput) || 0;
    patch({ contractRange: { min, max, basis: rangeFetch.status === 'ok' ? rangeFetch.basis : '' } });
    if (draft.dealId && rangeFetch.status === 'idle') void fetchRange(draft.dealId, { announce: false });
  };

  const handleContractAmount = (raw: string) => {
    setContractAmountInput(raw);
    const value = Number(raw) || 0;
    patch(draft.contractRange
      ? { contractAmount: value, contractRange: { ...draft.contractRange, max: value } }
      : { contractAmount: value });
  };

  const handleBondType = (bondType: SuretyBondType) => {
    if (bondType === draft.bondType) return;
    const pct = defaultBondPercent(bondType, settings);
    setBondPercentInput(String(pct));
    patch({ bondType, bondPercent: pct });
  };

  const handleDesignInScope = (designInScope: boolean) => {
    const prevDefault = defaultScopeOfWork(draft.projectLabel, draft.designInScope);
    const scopeOfWork = draft.scopeOfWork.trim() === prevDefault.trim() || !draft.scopeOfWork.trim()
      ? defaultScopeOfWork(draft.projectLabel, designInScope)
      : draft.scopeOfWork;
    patch({ designInScope, scopeOfWork });
  };

  const persist = async (toSave: SuretyBondRequestDraft, download: boolean) => {
    setSaving(download ? 'download' : 'save');
    setError(null);
    try {
      await onSave(toSave, { download });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the request');
    } finally {
      setSaving(null);
    }
  };

  const applyEnclosures = (result: SuretyEnclosureWizardResult) => {
    const changes: Partial<SuretyBondRequestDraft> = {
      enclosureDocs: result.docs,
      enclosures: mergeEnclosureSelections(draft.enclosures, result.docs),
    };
    if (result.startDate !== undefined) changes.startDate = result.startDate;
    if (result.completionDate !== undefined) changes.completionDate = result.completionDate;
    const next: SuretyBondRequestDraft = { ...draft, ...changes };
    next.bondAmount = computeBondAmount(next.contractAmount, next.bondPercent);
    setDraft(next);
    const mode = wizard;
    setWizard(null);
    // The wizard interrupted a download — finish it with the enclosure in place.
    if (mode === 'predownload') void persist(next, true);
  };

  const removeGeneratedEnclosure = (which: 'schedule' | 'contractSummary') => {
    const docs = { ...(draft.enclosureDocs ?? {}), [which]: null };
    const hasAny = !!docs.schedule || !!docs.contractSummary;
    patch({
      enclosureDocs: hasAny ? docs : null,
      enclosures: mergeEnclosureSelections(draft.enclosures, hasAny ? docs : null),
    });
  };

  const submit = async (download: boolean) => {
    const found = validateSuretyBondRequest(draft);
    setProblems(found);
    if (found.length) return;
    // "Project schedule" is listed as an enclosure but has not been built —
    // the PDF would reference a page that does not exist. Build it first.
    if (download && needsScheduleBeforeDownload(draft)) {
      setWizard('predownload');
      return;
    }
    await persist(draft, download);
  };

  // Opened from the list's Download button: head straight for the PDF.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!autoDownload || autoRan.current) return;
    autoRan.current = true;
    void submit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload]);

  const isBid = draft.bondType === 'bid';
  const agentMissing = !draft.agent.agency.trim() && !draft.agent.contact.trim();

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal reg-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={existing ? 'Edit bond request' : 'New bond request'}
      >
        <div className="fin-modal-header">
          <h3>{existing ? 'Edit bond request' : 'New bond request'}</h3>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <div className="fin-modal-error">{error}</div>}
          {problems.length > 0 && (
            <ul className="reg-modal-problems">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {agentMissing && (
            <p className="fin-modal-help">
              No bond agent is saved yet. The letter will print without an addressee until you add one
              under the gear icon on the Regulatory page.
            </p>
          )}

          <fieldset className="fin-modal-fieldset">
            <legend>Project and bond</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="sbr-project">Project <FieldHelp text="project" /></label>
                <select
                  id="sbr-project"
                  className="fin-modal-select"
                  value={draft.dealId}
                  onChange={(e) => handlePickProject(e.target.value)}
                  disabled={Boolean(existing)}
                  autoFocus={!existing}
                >
                  <option value="">Pick a project…</option>
                  {(
                    [
                      ['contracted', 'Under contract'],
                      ['preconstruction', 'Preconstruction'],
                      ['estimating', 'Estimating'],
                    ] as [ProjectPLGroup, string][]
                  ).map(([group, label]) =>
                    projectGroups[group].length ? (
                      <optgroup key={group} label={label}>
                        {projectGroups[group].map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </optgroup>
                    ) : null,
                  )}
                </select>
                {!existing && (
                  <span className="fin-modal-help">
                    Estimating and precon jobs prefill from the current estimate and default to a bid bond.
                  </span>
                )}
                {existing && (
                  <span className="fin-modal-help">Project is fixed on a saved request. Start a new request to bond a different job.</span>
                )}
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label">Bond type <FieldHelp text="bondType" /></label>
                <div className="reg-type-toggle" role="group" aria-label="Bond type">
                  {(['performance_payment', 'bid'] as SuretyBondType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={draft.bondType === t ? 'active' : ''}
                      onClick={() => handleBondType(t)}
                    >
                      {t === 'bid' ? 'Bid bond' : 'Performance & Payment'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-letter-date">Letter date <FieldHelp text="letterDate" align="right" /></label>
                <input
                  id="sbr-letter-date"
                  className="fin-modal-input"
                  type="date"
                  value={draft.letterDate}
                  onChange={(e) => patch({ letterDate: e.target.value })}
                />
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-contract">
                  {isBid ? 'Bid amount' : 'Contract amount'}{rangeOn ? ' (high end)' : ''} <FieldHelp text="contractAmount" />
                </label>
                <input
                  id="sbr-contract"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={contractAmountInput}
                  onChange={(e) => handleContractAmount(e.target.value)}
                />
                <span className="fin-modal-help">
                  {rangeOn
                    ? 'The bond is sized from the high end so it covers every selection.'
                    : 'Prefilled from the signed proposal plus approved change orders.'}
                </span>
              </div>

              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-pct">Bond percent <FieldHelp text="bondPercent" align="right" /></label>
                <input
                  id="sbr-pct"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={bondPercentInput}
                  onChange={(e) => {
                    setBondPercentInput(e.target.value);
                    patch({ bondPercent: Number(e.target.value) || 0 });
                  }}
                />
                <span className="fin-modal-help">{isBid ? 'Usually 5–10% of the bid.' : '100% of the contract price for each bond.'}</span>
              </div>

              <div className="fin-modal-field full">
                <div className="reg-range">
                  <div className="reg-range-head">
                    <label className="fin-modal-toggle">
                      <input type="checkbox" checked={rangeOn} onChange={(e) => toggleRange(e.target.checked)} disabled={!draft.dealId} />
                      Quote a {isBid ? 'bid' : 'contract'} range in the letter <FieldHelp text="contractRange" />
                    </label>
                    <button
                      type="button"
                      className="fin-secondary-btn"
                      disabled={!draft.dealId || rangeFetch.status === 'loading'}
                      onClick={() => void fetchRange(draft.dealId, { announce: true })}
                      title="Re-read the proposal and recompute the low and high across the client's selections."
                    >
                      {rangeFetch.status === 'loading' ? <Loader2 size={12} className="reg-spin" /> : <RefreshCw size={12} />} Recompute from proposal
                    </button>
                  </div>
                  {rangeOn && (
                    <>
                      <div className="fin-modal-field">
                        <label className="fin-modal-label" htmlFor="sbr-range-min">Low end</label>
                        <input
                          id="sbr-range-min"
                          className="fin-modal-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={rangeMinInput}
                          onChange={(e) => {
                            setRangeMinInput(e.target.value);
                            if (draft.contractRange) patch({ contractRange: { ...draft.contractRange, min: Number(e.target.value) || 0 } });
                          }}
                        />
                      </div>
                      <div className="fin-modal-field">
                        <label className="fin-modal-label" htmlFor="sbr-range-basis">Depends on</label>
                        <input
                          id="sbr-range-basis"
                          className="fin-modal-input"
                          value={draft.contractRange?.basis ?? ''}
                          onChange={(e) => {
                            if (draft.contractRange) patch({ contractRange: { ...draft.contractRange, basis: e.target.value } });
                          }}
                          placeholder="structure arrangement, species, installation"
                        />
                      </div>
                    </>
                  )}
                  <p className="reg-range-hint">
                    {rangeFetch.status === 'loading' && 'Reading the proposal…'}
                    {rangeFetch.status === 'ok' && (
                      <>Proposal still open to the client — price moves with <strong>{rangeFetch.basis}</strong> ({rangeFetch.scenarios} combinations checked).</>
                    )}
                    {rangeFetch.status === 'none' && rangeFetch.reason}
                    {rangeFetch.status === 'error' && `Could not read the proposal: ${rangeFetch.message}`}
                    {rangeFetch.status === 'idle' && (draft.dealId
                      ? 'Use the range when the client has not signed and can still change the arrangement, options, species, or installation.'
                      : 'Pick a project to read its proposal.')}
                  </p>
                </div>
              </div>

              <div className="fin-modal-field full">
                <div className="reg-amount-preview" title={SURETY_FIELD_HELP.bondAmount}>
                  <span>{SURETY_BOND_TYPE_LABELS[draft.bondType]}{isBid ? '' : ' (each)'}{activeContractRange(draft) ? ' · up to' : ''}</span>
                  <strong>{formatBondMoney(draft.bondAmount)}</strong>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Dates</legend>
            <div className="fin-modal-grid">
              {isBid ? (
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="sbr-bid-date">Bid date <FieldHelp text="bidDate" /></label>
                  <input
                    id="sbr-bid-date"
                    className="fin-modal-input"
                    type="date"
                    value={draft.bidDate ?? ''}
                    onChange={(e) => patch({ bidDate: e.target.value || null })}
                  />
                </div>
              ) : (
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="sbr-contract-date">Contract date <FieldHelp text="contractDate" /></label>
                  <input
                    id="sbr-contract-date"
                    className="fin-modal-input"
                    type="date"
                    value={draft.contractDate ?? ''}
                    onChange={(e) => patch({ contractDate: e.target.value || null })}
                  />
                </div>
              )}
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-needed">Bond needed by <FieldHelp text="neededBy" align="right" /></label>
                <input
                  id="sbr-needed"
                  className="fin-modal-input"
                  type="date"
                  value={draft.neededBy ?? ''}
                  onChange={(e) => patch({ neededBy: e.target.value || null })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-start">Start <FieldHelp text="startDate" /></label>
                <input
                  id="sbr-start"
                  className="fin-modal-input"
                  type="date"
                  value={draft.startDate ?? ''}
                  onChange={(e) => patch({ startDate: e.target.value || null })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-completion">Substantial completion <FieldHelp text="completionDate" align="right" /></label>
                <input
                  id="sbr-completion"
                  className="fin-modal-input"
                  type="date"
                  value={draft.completionDate ?? ''}
                  onChange={(e) => patch({ completionDate: e.target.value || null })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-warranty">Warranty months <FieldHelp text="warrantyMonths" /></label>
                <input
                  id="sbr-warranty"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="1"
                  value={warrantyInput}
                  onChange={(e) => {
                    setWarrantyInput(e.target.value);
                    patch({ warrantyMonths: Math.max(0, Math.round(Number(e.target.value) || 0)) });
                  }}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Obligee and site</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-obligee-role">Obligee is the <FieldHelp text="obligeeRole" /></label>
                <select
                  id="sbr-obligee-role"
                  className="fin-modal-select"
                  value={draft.obligee.role}
                  onChange={(e) => patch({ obligee: { ...draft.obligee, role: e.target.value as SuretyObligeeRole } })}
                >
                  {(Object.keys(SURETY_OBLIGEE_ROLE_LABELS) as SuretyObligeeRole[]).map((r) => (
                    <option key={r} value={r}>{SURETY_OBLIGEE_ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-obligee-name">Obligee name <FieldHelp text="obligeeName" align="right" /></label>
                <input
                  id="sbr-obligee-name"
                  className="fin-modal-input"
                  value={draft.obligee.name}
                  onChange={(e) => patch({ obligee: { ...draft.obligee, name: e.target.value } })}
                  placeholder={draft.obligee.role === 'general_contractor' ? 'General contractor' : 'Owner'}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-obligee-address">Obligee address <FieldHelp text="obligeeAddress" /></label>
                <textarea
                  id="sbr-obligee-address"
                  className="fin-modal-textarea"
                  rows={2}
                  value={draft.obligee.address}
                  onChange={(e) => patch({ obligee: { ...draft.obligee, address: e.target.value } })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-site">Project site address <FieldHelp text="siteAddress" align="right" /></label>
                <textarea
                  id="sbr-site"
                  className="fin-modal-textarea"
                  rows={2}
                  value={draft.siteAddress}
                  onChange={(e) => patch({ siteAddress: e.target.value })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-ae">Architect / engineer of record <FieldHelp text="architectEngineer" /></label>
                <input
                  id="sbr-ae"
                  className="fin-modal-input"
                  value={draft.architectEngineer}
                  onChange={(e) => patch({ architectEngineer: e.target.value })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-form">Bond form required <FieldHelp text="bondForm" align="right" /></label>
                <input
                  id="sbr-form"
                  className="fin-modal-input"
                  value={draft.bondForm}
                  onChange={(e) => patch({ bondForm: e.target.value })}
                  placeholder="AIA A312"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Scope and contract terms</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="sbr-scope">Scope of work <FieldHelp text="scopeOfWork" /></label>
                <textarea
                  id="sbr-scope"
                  className="fin-modal-textarea"
                  rows={2}
                  value={draft.scopeOfWork}
                  onChange={(e) => patch({ scopeOfWork: e.target.value })}
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-toggle">
                  <input
                    type="checkbox"
                    checked={draft.designInScope}
                    onChange={(e) => handleDesignInScope(e.target.checked)}
                  />
                  Design is in our scope <FieldHelp text="designInScope" />
                </label>
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-sub">Subcontracted % of contract <FieldHelp text="subcontractedPercent" align="right" /></label>
                <input
                  id="sbr-sub"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={subPctInput}
                  onChange={(e) => {
                    setSubPctInput(e.target.value);
                    patch({ subcontractedPercent: parseNumOrNull(e.target.value) });
                  }}
                  placeholder="Optional"
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-retainage">Retainage % <FieldHelp text="retainagePercent" /></label>
                <input
                  id="sbr-retainage"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={retainageInput}
                  onChange={(e) => {
                    setRetainageInput(e.target.value);
                    patch({ retainagePercent: parseNumOrNull(e.target.value) });
                  }}
                  placeholder="Optional"
                />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sbr-ld">Liquidated damages $/day <FieldHelp text="liquidatedDamagesPerDay" align="right" /></label>
                <input
                  id="sbr-ld"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="1"
                  value={ldInput}
                  onChange={(e) => {
                    setLdInput(e.target.value);
                    patch({ liquidatedDamagesPerDay: parseNumOrNull(e.target.value) });
                  }}
                  placeholder="Optional"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Enclosures and notes <FieldHelp text="enclosures" /></legend>
            <div className="reg-generated-row">
              <button
                type="button"
                className="fin-secondary-btn"
                onClick={() => setWizard('manual')}
                disabled={!draft.dealId}
                title={draft.dealId ? 'Generate the project schedule and contract summary pages.' : 'Pick a project first.'}
              >
                <Sparkles size={13} /> {draft.enclosureDocs?.schedule || draft.enclosureDocs?.contractSummary ? 'Rebuild enclosures…' : 'Build enclosures…'}
              </button>
              {draft.enclosureDocs?.schedule && (
                <span className="reg-enclosure-chip" title={`${draft.enclosureDocs.schedule.phases.length} phases · ${longDateFromYmd(scheduleStartDate(draft.enclosureDocs.schedule.phases))} → ${longDateFromYmd(scheduleCompletionDate(draft.enclosureDocs.schedule.phases))}`}>
                  A · Project schedule
                  {draft.enclosureDocs.schedule.phases.some((p) => p.source === 'simulated') ? <em>simulated</em> : null}
                  <button type="button" aria-label="Remove project schedule enclosure" onClick={() => removeGeneratedEnclosure('schedule')}><X size={11} /></button>
                </span>
              )}
              {draft.enclosureDocs?.contractSummary && (
                <span className="reg-enclosure-chip" title={`Contract value ${formatBondMoney(draft.enclosureDocs.contractSummary.currentContractValue)} · ${draft.enclosureDocs.contractSummary.changeOrders.length} approved CO(s)`}>
                  B · Contract summary
                  <button type="button" aria-label="Remove contract summary enclosure" onClick={() => removeGeneratedEnclosure('contractSummary')}><X size={11} /></button>
                </span>
              )}
            </div>
            <div className="reg-enclosure-list">
              {draft.enclosures.map((item, i) => (
                isGeneratedEnclosureLabel(item) ? null : (
                  <div key={i} className="reg-enclosure-row">
                    <input
                      className="fin-modal-input"
                      value={item}
                      onChange={(e) => {
                        const enclosures = [...draft.enclosures];
                        enclosures[i] = e.target.value;
                        patch({ enclosures });
                      }}
                      placeholder="Enclosure attached by hand, e.g. Owner's bond form"
                    />
                    <button
                      type="button"
                      className="fin-icon-btn danger"
                      aria-label="Remove enclosure"
                      onClick={() => patch({ enclosures: draft.enclosures.filter((_, j) => j !== i) })}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              ))}
              <div>
                <button
                  type="button"
                  className="fin-secondary-btn"
                  onClick={() => patch({ enclosures: [...draft.enclosures, ''] })}
                >
                  <Plus size={13} /> Add enclosure
                </button>
              </div>
            </div>
            <div className="fin-modal-field" style={{ marginTop: 10 }}>
              <label className="fin-modal-label" htmlFor="sbr-notes">Additional note to the agent <FieldHelp text="notes" /></label>
              <textarea
                id="sbr-notes"
                className="fin-modal-textarea"
                rows={2}
                value={draft.notes}
                onChange={(e) => patch({ notes: e.target.value })}
                placeholder="Optional — printed after the details table."
              />
            </div>
          </fieldset>
        </div>

        <div className="fin-modal-footer">
          <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving !== null}>
            Cancel
          </button>
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={() => submit(false)} disabled={saving !== null}>
              {saving === 'save' ? 'Saving…' : existing ? 'Save changes' : 'Save draft'}
            </button>
            <button type="button" className="fin-primary-btn" onClick={() => submit(true)} disabled={saving !== null}>
              <Download size={14} /> {saving === 'download' ? 'Preparing…' : 'Save and download PDF'}
            </button>
          </div>
        </div>
      </div>
      {wizard === 'manual' && (
        <SuretyEnclosureWizardModal
          request={draft}
          scheduleTasks={scheduleTasks}
          taskGroups={taskGroups}
          onClose={() => setWizard(null)}
          onApply={applyEnclosures}
        />
      )}
      {wizard === 'predownload' && (
        <SuretyEnclosureWizardModal
          request={draft}
          scheduleTasks={scheduleTasks}
          taskGroups={taskGroups}
          preset={{
            schedule: true,
            contractSummary: !!draft.enclosureDocs?.contractSummary || wantsContractSummaryEnclosure(draft.enclosures),
          }}
          startAt="schedule"
          intro="This request lists the project schedule as an enclosure but it has not been built yet. Confirm the phases below and the schedule page will be added to the PDF."
          applyLabel="Apply and download PDF"
          onClose={() => setWizard(null)}
          onApply={applyEnclosures}
        />
      )}
    </div>
  );
};

export default SuretyBondRequestModal;
